/**
 * The navigation model.
 *
 * Mirrors the prototype's `NAV` in `docs/html/assets/js/app.js` entry for entry,
 * but with one decisive difference: **the prototype hard-codes which roles see
 * each item; this does not.** Visibility comes from `screen`, checked against
 * `/auth/me`. That is what keeps the sidebar and the permission model from
 * drifting apart — add a grant in the admin console and the item appears, with
 * no front-end deploy.
 *
 * `screen` is the `iam.app_screen.key`. Most nav ids *are* the screen key; the
 * Administration group differs in punctuation only (`admin-audit` in the
 * prototype's nav, `admin.audit` as the screen key), which is why the two are
 * separate fields here rather than one.
 */

export const NAV_GROUPS = [
  {
    group: 'Workspace',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: 'bi-grid-1x2-fill', to: '/', screen: 'dashboard' },
      { id: 'feed', label: 'Activity Feed', icon: 'bi-broadcast', to: '/feed', screen: 'feed' },
      /* Calendar used to be its own nav item (`to: '/calendar'`). Same rows as
         Activities — a month grid instead of a table — now a List/Calendar
         toggle inside the Activities screen itself, gated on the same
         `calendar` screen permission, the same move already made for
         Pipeline below. See ActivitiesPage.jsx. */
      /* Has no prototype counterpart — `teams` isn't in docs/html/assets/js/app.js's
         NAV model at all. A sales manager self-serving their own team's
         creation/roster is a requirement with no design mock to mirror; see
         salescrmapi's teams module build notes. Seeded manager grant is
         `teams:view/create/edit` at team scope, so this only appears for
         admin and manager, not rep/exec. */
      { id: 'teams', label: 'My Team', icon: 'bi-people-fill', to: '/teams', screen: 'teams' },
    ],
  },
  {
    group: 'Sell',
    items: [
      { id: 'leads', label: 'Leads', icon: 'bi-magnet-fill', to: '/leads', screen: 'leads' },
      { id: 'accounts', label: 'Accounts', icon: 'bi-building', to: '/accounts', screen: 'accounts' },
      { id: 'contacts', label: 'Contacts', icon: 'bi-person-lines-fill', to: '/contacts', screen: 'contacts' },
      /* Pipeline used to be its own nav item (`to: '/pipeline'`). It's the same
         deals as Opportunities shown as a kanban instead of a table — now a
         List/Board toggle inside the Opportunities screen itself, gated on
         the same `pipeline` screen permission, rather than a second entry a
         click away that just came back here. See OpportunitiesPage.jsx. */
      { id: 'opportunities', label: 'Opportunities', icon: 'bi-briefcase-fill', to: '/opportunities', screen: 'opportunities' },
      { id: 'quotations', label: 'Quotations', icon: 'bi-file-earmark-text-fill', to: '/quotations', screen: 'quotations' },
    ],
  },
  {
    group: 'Engage',
    items: [
      { id: 'activities', label: 'Activities', icon: 'bi-list-check', to: '/activities', screen: 'activities' },
      /* Follow-ups used to be its own nav item (`to: '/follow-ups'`). Same
         move as Calendar and Pipeline: a List/Calendar/Follow-ups toggle
         inside the Activities screen itself, gated on the same `followups`
         screen permission, rather than a second entry a click away. See
         ActivitiesPage.jsx. */
      { id: 'templates', label: 'Email Templates', icon: 'bi-envelope-paper-fill', to: '/email-templates', screen: 'templates' },
      /* Has no prototype counterpart either — same "real requirement, no
         design mock" situation as My Team above. See
         salescrmapi/docs/modules/emailInbox.model.md. */
      { id: 'email', label: 'Email', icon: 'bi-inbox-fill', to: '/email', screen: 'email' },
      { id: 'campaigns', label: 'Campaigns', icon: 'bi-megaphone-fill', to: '/campaigns', screen: 'campaigns' },
    ],
  },
  {
    group: 'Analyse',
    items: [
      /* The one nested nav item in the app — see `visibleNav`'s handling of
         `children` below and `Sidebar`/`NavGroupItem` in `AppShell.jsx`.
         `forecast`/`territory` used to be their own flat top-level items,
         each gated on its own seeded screen key (`forecast`/`territory`);
         folded in here as real reports reading `crm.target`/`md.territory`
         through `reports:view` instead — see
         `salescrmapi/docs/modules/reports.model.md` §3. `targets` stays a
         separate top-level item: it's a CRUD screen (create/edit a quota),
         not a report. */
      {
        id: 'reports', label: 'Reports', icon: 'bi-bar-chart-fill', to: '/reports', screen: 'reports',
        children: [
          { id: 'reports-overview', label: 'Overview', to: '/reports' },
          { id: 'reports-leads', label: 'Leads', to: '/reports/leads' },
          { id: 'reports-opportunities', label: 'Opportunities', to: '/reports/opportunities' },
          { id: 'reports-activities', label: 'Activities', to: '/reports/activities' },
          { id: 'reports-revenue', label: 'Revenue', to: '/reports/revenue' },
          { id: 'reports-forecast', label: 'Forecast', to: '/reports/forecast' },
          { id: 'reports-territories', label: 'Territories', to: '/reports/territories' },
        ],
      },
      { id: 'targets', label: 'Targets & Quotas', icon: 'bi-bullseye', to: '/targets', screen: 'targets' },
    ],
  },
  {
    group: 'Administration',
    items: [
      { id: 'admin-users', label: 'Users & Roles', icon: 'bi-person-gear', to: '/admin/users', screen: 'admin.users' },
      /* No prototype counterpart — same deviation as 'teams' and 'email':
         crm.partner has existed since db/schema/07 with no screen until
         docs/modules/partnerLeads.model.md §3. '/admin/partners' is a real
         frontend route, not an *.html file, because there is no mock to
         route to. */
      { id: 'admin-partners', label: 'Partners', icon: 'bi-people', to: '/admin/partners', screen: 'admin.partners' },
      { id: 'admin-master', label: 'Master Data', icon: 'bi-database-fill-gear', to: '/admin/master-data', screen: 'admin.master' },
      { id: 'admin-letterhead', label: 'Letterhead', icon: 'bi-file-earmark-richtext', to: '/admin/letterhead', screen: 'admin.master' },
      { id: 'admin-workflow', label: 'Workflow Builder', icon: 'bi-diagram-3-fill', to: '/admin/workflows', screen: 'admin.workflow' },
      { id: 'admin-audit', label: 'Audit Log', icon: 'bi-clock-history', to: '/admin/audit', screen: 'admin.audit' },
      { id: 'admin-security', label: 'Security', icon: 'bi-shield-check', to: '/admin/security', screen: 'admin.security' },
    ],
  },
  {
    group: 'Help',
    items: [
      /* `screen: null`, not a real `iam.app_screen` key — a user manual isn't
         a permission, so this is the one item `visibleNav` never filters,
         rather than inventing a fake grant every role would need seeding
         just to see it. Reachable at every role, including a user with no
         screens granted at all. */
      { id: 'help', label: 'Help & Guide', icon: 'bi-life-preserver', to: '/help', screen: null },
    ],
  },
];

/**
 * Drop everything the caller cannot open, then drop any group left empty.
 *
 * An empty group heading is worse than no heading: it tells the user a section
 * exists and gives them nothing to click.
 *
 * @param {(screenKey:string)=>boolean} canOpen usually `useAuth().canOpen`
 * @param {Record<string, number>} [badges] screen key → count, for the pills
 */
export function visibleNav(canOpen, badges = {}) {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items
      .filter((item) => item.screen == null || canOpen(item.screen))
      .map((item) => ({ ...item, badge: item.screen == null ? undefined : badges[item.screen] })),
  })).filter((group) => group.items.length > 0);
}

/**
 * Where to send someone after they sign in.
 *
 * `landing_screen` comes from the highest-priority role they hold — which is why
 * a rep covering as a manager lands on the manager dashboard without anyone
 * editing their primary role. Falls back to the dashboard when the role names a
 * screen this build has no route for.
 *
 * @param {string|null} landingScreen e.g. 'dashboard.manager'
 */
export function landingRouteFor(landingScreen) {
  const map = {
    'dashboard.admin': '/dashboard/admin',
    'dashboard.manager': '/dashboard/manager',
    'dashboard.rep': '/dashboard/rep',
    'dashboard.exec': '/dashboard/exec',
  };
  return map[landingScreen] ?? '/';
}
